<template>
  <div class="forgot-wrapper d-flex justify-content-center align-items-center min-vh-100">
    <div class="card p-4 shadow-lg rounded-4 bg-white bg-opacity-75" style="max-width: 400px; width: 100%;">
      <h4 class="text-center mb-3">Ubah Password</h4>

      <!-- Input Email -->
      <div class="mb-3">
        <label for="email" class="form-label">Masukkan Email</label>
        <input
          v-model="email"
          @input="validateEmail"
          type="email"
          id="email"
          class="form-control"
          :class="{ 'is-invalid': errorEmail }"
        />
        <div class="invalid-feedback" v-if="errorEmail">{{ errorEmail }}</div>
      </div>

      <!-- Input Password Lama -->
      <div class="mb-3">
        <label for="oldPassword" class="form-label">Password Lama</label>
        <div class="input-group">
          <input
            v-model="oldPassword"
            :type="showOldPassword ? 'text' : 'password'"
            id="oldPassword"
            class="form-control"
            @input="validateOldPassword"
            :class="{ 'is-invalid': errorOldPassword }"
          />
          <button class="btn btn-outline-secondary" type="button" @click="showOldPassword = !showOldPassword">
            {{ showOldPassword ? '🙈' : '👁️' }}
          </button>
          <div class="invalid-feedback" v-if="errorOldPassword">{{ errorOldPassword }}</div>
        </div>
      </div>

      <!-- Input Password Baru -->
      <div class="mb-3">
        <label for="newPassword" class="form-label">Password Baru</label>
        <div class="input-group">
          <input
            v-model="newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            id="newPassword"
            class="form-control"
            @input="validateNewPassword"
            :class="{ 'is-invalid': errorNewPassword }"
          />
          <button class="btn btn-outline-secondary" type="button" @click="showNewPassword = !showNewPassword">
            {{ showNewPassword ? '🙈' : '👁️' }}
          </button>
          <div class="invalid-feedback" v-if="errorNewPassword">{{ errorNewPassword }}</div>
        </div>
      </div>

      <!-- Input Ulangi Password Baru -->
      <div class="mb-3">
        <label for="confirmPassword" class="form-label">Ulangi Password Baru</label>
        <div class="input-group">
          <input
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            id="confirmPassword"
            class="form-control"
            @input="validateConfirmPassword"
            :class="{ 'is-invalid': errorConfirmPassword }"
          />
          <button class="btn btn-outline-secondary" type="button" @click="showConfirmPassword = !showConfirmPassword">
            {{ showConfirmPassword ? '🙈' : '👁️' }}
          </button>
          <div class="invalid-feedback" v-if="errorConfirmPassword">{{ errorConfirmPassword }}</div>
        </div>
      </div>

      <!-- Tombol Submit -->
      <button 
        @click="submit" 
        class="btn btn-primary w-100" 
        :disabled="loading || errorEmail || errorOldPassword || errorNewPassword || errorConfirmPassword || !email || !oldPassword || !newPassword || !confirmPassword"
      >
        <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
        Ubah Password
      </button>

      <div class="text-center mt-3">
        <router-link to="/">⬅ Kembali ke Login</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import axios from 'axios'
import Swal from 'sweetalert2'
import { useRouter } from 'vue-router'

const email = ref('')
const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const errorEmail = ref('')
const errorOldPassword = ref('')
const errorNewPassword = ref('')
const errorConfirmPassword = ref('')

const showOldPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)

const loading = ref(false)
const router = useRouter()

// Validasi email
const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

const validateEmail = () => {
  if (!email.value) {
    errorEmail.value = 'Email wajib diisi'
  } else if (!isValidEmail(email.value)) {
    errorEmail.value = 'Format email tidak valid'
  } else {
    errorEmail.value = ''
  }
}

// Validasi password lama
const validateOldPassword = () => {
  if (!oldPassword.value) {
    errorOldPassword.value = 'Password lama wajib diisi'
  } else if (oldPassword.value.length < 6) {
    errorOldPassword.value = 'Minimal 6 karakter'
  } else {
    errorOldPassword.value = ''
  }
}

// Validasi password baru
const validateNewPassword = () => {
  if (!newPassword.value) {
    errorNewPassword.value = 'Password baru wajib diisi'
  } else if (newPassword.value.length < 6) {
    errorNewPassword.value = 'Minimal 6 karakter'
  } else {
    errorNewPassword.value = ''
  }
}

// Validasi konfirmasi password baru
const validateConfirmPassword = () => {
  if (!confirmPassword.value) {
    errorConfirmPassword.value = 'Ulangi password wajib diisi'
  } else if (confirmPassword.value.length < 6) {
    errorConfirmPassword.value = 'Minimal 6 karakter'
  } else if (confirmPassword.value !== newPassword.value) {
    errorConfirmPassword.value = 'Password tidak sama'
  } else {
    errorConfirmPassword.value = ''
  }
}

// Submit form ubah password
const submit = async () => {
  validateEmail()
  validateOldPassword()
  validateNewPassword()
  validateConfirmPassword()

  if (errorEmail.value || errorOldPassword.value || errorNewPassword.value || errorConfirmPassword.value) return

  loading.value = true
  try {
    const response = await axios.post('https://metatekapps.metateknologi.com/api-vms-change-password.php', {
      email: email.value,
      pass_old: oldPassword.value,
      pass_new: newPassword.value,
      pass_confirm: confirmPassword.value
    })

    if (response.data.status === 200) {
      await Swal.fire({
        icon: 'success',
        title: 'Berhasil!',
        text: 'Password berhasil diubah. Cek email untuk melihat password baru anda',
        confirmButtonText: 'OK'
      })
      router.push('/') // setelah klik OK, ke login
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: response.data.message || 'Permintaan gagal.'
      })
    }
  } catch (err) {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: err.response?.data?.message || 'Gagal menghubungi server.'
    })
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.forgot-wrapper {
  background: url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1350&q=80') no-repeat center center;
  background-size: cover;
}

.card {
  backdrop-filter: blur(10px);
}
</style>
